import { useCallback } from 'react';
import { useErrorToastStore } from '@/shared/stores/errorToastStore';
import { extractErrorMessage, getErrorStatusCode } from '@/shared/lib/errorMessages';

/**
 * Hook for manually triggering error toasts
 *
 * ## Usage
 *
 * ```typescript
 * const { showError, showAxiosError } = useErrorToast();
 *
 * // Manual error with status code
 * showError(400, "Invalid pond name");
 *
 * // Extract from axios error
 * try {
 *   await api.createPond(data);
 * } catch (error) {
 *   showAxiosError(error);
 * }
 * ```
 */
export function useErrorToast() {
  const { addToast } = useErrorToastStore();

  /**
   * Show error toast with custom message and status code
   */
  const showError = useCallback(
    (statusCode: number, message: string) => {
      addToast(statusCode, message);
    },
    [addToast]
  );

  /**
   * Extract error from axios/unknown error and show toast
   */
  const showAxiosError = useCallback(
    (error: unknown) => {
      const statusCode = getErrorStatusCode(error);
      // Only use status code if it's an actual HTTP error
      // For client-side errors, use 0 to indicate it's not a server error
      const code = statusCode ?? 0;
      const message = extractErrorMessage(error);
      addToast(code, message);
    },
    [addToast]
  );

  return {
    showError,
    showAxiosError,
  };
}
